import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { apiErrorMessage } from '../../utils/apiError';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { CalendarClock, Loader2 } from 'lucide-react';
import { DateOfBirthField, validateDobIso } from './DateOfBirthField';

/**
 * P0-DOB-SELF-DECLARATION-01 / P0-DOB-ONBOARDING-01 — canonical
 * date-of-birth capture.
 *
 * Writes ONLY to `persons.date_of_birth` through
 * `PUT /api/patient/profile/date-of-birth`, which re-uses
 * `person_service.set_patient_date_of_birth` → `age_policy_service.assert_adult`.
 * The person is resolved server-side from the session; no patient id is sent.
 *
 * Used by every patient-facing entry point (post-login completion gate,
 * Clinical profile → Personal information, offer-acceptance fallback) so
 * there is exactly one field, one authority and one validation path.
 */
export const DateOfBirthGate = ({ code, message, onSaved, testIdPrefix = 'dob' }) => {
  const { t } = useTranslation();
  const [dob, setDob] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const blockedByAge = code === 'PATIENT_UNDER_18';

  const save = async () => {
    setError(null);
    const problem = validateDobIso(dob);
    if (problem) {
      setError(t(problem));
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put('/patient/profile/date-of-birth', { date_of_birth: dob });
      if (onSaved) onSaved(data?.eligibility || null);
    } catch (e) {
      setError(apiErrorMessage(e, t('patient.dob.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Alert
      className={blockedByAge ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'}
      data-testid={`${testIdPrefix}-gate`}
    >
      <CalendarClock className="h-4 w-4" />
      <AlertDescription>
        <p className="text-sm text-slate-800" data-testid={`${testIdPrefix}-gate-message`}>
          {message || t('patient.dob.explain')}
        </p>
        {!blockedByAge && (
          <>
            {message && message !== t('patient.dob.explain') && (
              <p className="mt-1 text-xs text-slate-600">{t('patient.dob.explain')}</p>
            )}
            <div className="mt-3 flex flex-wrap items-end gap-3">
              <DateOfBirthField
                value={dob}
                onChange={setDob}
                disabled={saving}
                testIdPrefix={testIdPrefix}
              />
              <Button
                onClick={save}
                disabled={saving || !dob}
                className="mb-5"
                data-testid={`${testIdPrefix}-save-btn`}
              >
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('patient.dob.saveAndContinue')}
              </Button>
            </div>
            <p className="mt-2 text-[11px] text-slate-500">{t('patient.dob.minAgeNote')}</p>
          </>
        )}
        {error && (
          <p className="mt-2 text-xs text-red-600" data-testid={`${testIdPrefix}-error`}>{error}</p>
        )}
      </AlertDescription>
    </Alert>
  );
};

export default DateOfBirthGate;
